// root '../../../';

// dependencies
import { Child } from 'pym.js';
import Zion from './utils/Zion';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic

// styles
import './styles/graphic.scss';

/* develblock:start */

import * as d3 from 'd3';
import { format } from 'd3-format';
import meta from './data/meta.json';
import { stylePremium } from './utils/helpers';
import { bandAxis, linearAxis } from '../../../factory/axes';

import './styles/graphic.scss';

// formatD3Time();
d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

const fmtComma = format(',');
const getHeadline = (cur, prev) => {
  if (cur.amt === 0) return null;
  // sample headline from previous graphic
  // US economy added 223,000 jobs in December, unemployment rate dropped to 3.5%
  const rateDir = cur.rate - prev.rate;
  return [
    'US economy',
    cur.amt > 0 ? 'added' : 'lost',
    fmtComma(Math.abs(cur.amt)),
    `job${cur.amt !== 1 ? 's' : ''}`,
    `in ${cur.month}.`,
    'Unemployment rate',
    `${(rateDir < 0) ? 'dropped to' : (rateDir > 0) ? 'rose to' : 'remains at'}`,
    `${cur.rate}%.`,
  ].join(' ');
};


const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}


let minValue;
let maxValue;


// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};


const CNNPolitics = false;
const CNNBusiness = false;

stylePremium(CNNPolitics, CNNBusiness);

let data;

const recessions = [
  {
    startYear: 2001, endYear: 2001, line1: 'Early 2000s', line2: 'recession',
  },
  {
    startYear: 2007, endYear: 2009, line1: 'Great', line2: 'Recession',
  },
  {
    startYear: 2020, endYear: 2020, line1: 'Covid-19', line2: 'recession',
  },
];

d3.json('https://ix.cnn.io/data/jobs-next/employment_situation_latest.json').then((jsondata) => {
  data = jsondata.annual_jobs_data.map(d => ({
    year: d.year,
    amt: d.jobs_added,
  })).sort((a, b) => a.year - b.year);

  minValue = Math.min(0, d3.min(data, d => d.amt)) * 1.09;
  maxValue = d3.max(data, d => d.amt) * 1.2;

  const lastYear = data[data.length - 1].year;
  const currentYear = new Date().getFullYear();
  const latestIsPartial = lastYear >= currentYear;

  const partialNote = latestIsPartial
    ? `The figure for ${lastYear} includes data through ${jsondata.month}. `
    : '';

  d3.select('p#timestamp').html(`Notes: Figures represent the change from December of the prior year to December, seasonally adjusted. ${partialNote}Data current as of ${jsondata.last_updated_str}.`);

  draw();
  window.onresize = draw;
});

function draw() {
  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const initialHeight = 400;
  const isMobile = initialWidth <= 500;

  // Extra top margin makes room for the endpoint annotation above the
  // final bar so it doesn't collide with the plot area above it.
  const margins = {
    top: 25,
    right: 20,
    bottom: 25,
    left: isMobile ? 34 : 44,
  };

  const chartWidth = initialWidth - margins.left - margins.right;
  const chartHeight = initialHeight - margins.top - margins.bottom;

  const padding = 0.15;

  const xAxisConfig = {
    orientation: 'bottom',
    range: [0, chartWidth],
    domain: data.map(d => d.year),
    padding,
  };

  const yAxisConfig = {
    orientation: 'left',
    range: [chartHeight, 0],
    domain: [Math.min(0, minValue), maxValue],
    nice: false,
  };

  const { axis: xAxis, scale: x } = bandAxis(xAxisConfig);
  const { axis: yAxis, scale: y } = linearAxis(yAxisConfig);

  const yTicks = isMobile ? 6 : 6;

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', initialHeight);

  const g = svg.append('g')
    .attr('transform', `translate(${margins.left},${margins.top})`);

  // More ticks: every other year reads fine at desktop bandwidth; mobile
  // gets a coarser step (every 4th year) so labels don't collide, but
  // still shows more than just the first/last.
  const tickStep = isMobile ? 6 : 4;
  const tickYears = data.map(d => d.year).filter(y => y % tickStep === 0);

  // X-axis
  g.append('g')
    .attr('class', 'x grid')
    .attr('transform', `translate(0,${chartHeight})`)
    .call(
      xAxis(x)
        .tickValues(tickYears)
        .tickFormat(d => d)
        .tickSize(4)
        .tickPadding(4),
    );

  // Y-axis: show the "k"/"M" unit on the top tick only, per style guide,
  // and keep the +/- since that's meaningful (gain vs. loss), not decorative
  const yTickValues = y.ticks(yTicks);
  const yMaxTick = Math.max(...yTickValues);
  const fmtY = (d) => {
    if (d === 0) return '0';
    const suffix = d === yMaxTick ? (Math.abs(d) >= 1e6 ? 'M' : 'k') : '';
    const short = Math.abs(d) >= 1e6
      ? d3.format('~r')(Math.abs(d) / 1e6)
      : Math.round(Math.abs(d) / 1000);
    return `${d > 0 ? '+' : '-'}${short}${suffix}`;
  };

  g.append('g')
    .attr('class', 'y grid')
    .call(
      yAxis(y)
        .tickValues(yTickValues)
        .tickSize(-chartWidth)
        .tickFormat(fmtY),
    );

  // Recession shading
  recessions.forEach((rec) => {
    const x1 = x(rec.startYear);
    const x2 = x(rec.endYear);
    if (x1 === undefined || x2 === undefined) return;

    g.append('rect')
      .attr('class', 'recession-band')
      .attr('x', x1)
      .attr('width', x2 + x.bandwidth() - x1)
      .attr('y', 0)
      .attr('height', chartHeight)
      .attr('fill', '#e3e3e3')
      .attr('fill-opacity', 0.3);

    if (!isMobile) {
      const labelX = x2 + x.bandwidth() + 6;
      const labelText = g.append('text')
        .attr('x', labelX)
        .attr('y', chartHeight - 22)
        .attr('class', 'label-recession');

      labelText.append('tspan')
        .attr('x', labelX)
        .attr('dy', 0)
        .text(rec.line1);

      labelText.append('tspan')
        .attr('x', labelX)
        .attr('dy', '1.2em')
        .text(rec.line2);
    }
  });

  // On mobile, one shared label above the Great Recession band
  if (isMobile) {
    const gr = recessions.find(r => r.startYear === 2007);
    if (gr) {
      const grX1 = x(gr.startYear);
      const grX2 = x(gr.endYear);
      if (grX1 !== undefined && grX2 !== undefined) {
        g.append('text')
          .attr('x', grX1 + (grX2 + x.bandwidth() - grX1) / 2)
          .attr('y', 20)
          .attr('text-anchor', 'middle')
          .attr('class', 'label-recession')
          .text('Recessions');
      }
    }
  }

  // Tooltip (HTML div, positioned relative to the chart container)
  const tooltip = container.append('div')
    .attr('class', 'tooltip')
    .style('display', 'none');

  // Bars
  const bars = g.append('g').attr('class', 'data');

  bars.selectAll('.bar')
    .data(data)
    .enter().append('rect')
    .attr('class', d => `bar ${d.amt >= 0 ? 'increase' : 'decrease'}`)
    .attr('x', d => x(d.year))
    .attr('y', d => y(d.amt > 0 ? d.amt : 0))
    .attr('width', x.bandwidth())
    .attr('height', d => Math.abs(y(d.amt) - y(0)))
    .on('mouseover', function onBarOver() {
      d3.select(this).classed('active', true);
      tooltip.style('display', 'block');
    })
    .on('mousemove', (d) => {
      const [mx, my] = d3.mouse(container.node());
      const sign = d.amt > 0 ? '+' : d.amt < 0 ? '-' : '';
      tooltip.html(`<strong>${d.year}</strong><br>${sign}${fmtComma(Math.abs(d.amt))} jobs`);

      // Flip to the left of the cursor when there isn't room to the right,
      // so tooltips on the most recent (rightmost) bars don't run off the chart
      const tooltipWidth = tooltip.node().offsetWidth;
      const left = (mx + 12 + tooltipWidth > initialWidth)
        ? mx - tooltipWidth - 12
        : mx + 12;

      tooltip
        .style('left', `${left}px`)
        .style('top', `${my - 38}px`);
    })
    .on('mouseout', function onBarOut() {
      d3.select(this).classed('active', false);
      tooltip.style('display', 'none');
    });

  // Endpoint annotation: year stacked over value, rendered as haloed SVG
  // text (no black box), with a leader line down to the top-center of the
  // final bar so it can sit well clear of the column instead of crowding it.
  const lastBar = data[data.length - 1];
  const endpointX = x(lastBar.year) + x.bandwidth() / 2; // top-center of the bar
  const barTopY = y(Math.max(lastBar.amt, 0));
  const latestIsPartial = lastBar.year >= new Date().getFullYear();
  const lastBarSign = lastBar.amt < 0 ? '-' : '+';
  const lastBarValue = Math.abs(lastBar.amt) >= 1e6
    ? `${d3.format('~r')(Math.abs(lastBar.amt) / 1e6)}M`
    : `${Math.round(Math.abs(lastBar.amt) / 1000)}k`;

  // Text-anchor 'end' at the bar's own x keeps the label from running off
  // the right edge of the chart, since this is always the rightmost bar.
  const endpointLabelY = barTopY - 50;

  const endpoint = g.append('g').attr('class', 'endpoint-annotation');

  endpoint.append('line')
    .attr('class', 'endpoint-leader')
    .attr('x1', endpointX)
    .attr('x2', endpointX)
    .attr('y1', endpointLabelY + 6)
    .attr('y2', barTopY - 4);

  endpoint.append('circle')
    .attr('class', 'endpoint-dot')
    .attr('cx', endpointX)
    .attr('cy', barTopY)
    .attr('r', 3);

  endpoint.append('text')
    .attr('class', 'endpoint-date')
    .attr('x', endpointX + 2)
    .attr('y', endpointLabelY)
    .attr('text-anchor', 'end')
    .attr('dy', '-1.2em')
    .text(`${lastBar.year}${latestIsPartial ? '' : ''}`);

  endpoint.append('text')
    .attr('class', 'endpoint-value')
    .attr('x', endpointX + 2)
    .attr('y', endpointLabelY)
    .attr('text-anchor', 'end')
    .text(`${lastBarSign}${lastBarValue}`);

  // Zero line if necessary
  if (y.domain()[0] < 0) {
    g.append('line')
      .attr('class', 'zero-bar')
      .attr('x1', 0)
      .attr('x2', chartWidth)
      .attr('y1', y(0))
      .attr('y2', y(0));
  }

  window.pymChild.sendHeight();
  d3.select('#root')
    .attr('class', 'ready');
}
